import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createShareLink, getShareLinks, revokeShareLink, ShareLink } from "../../services/UserDemoService";
import styles from "./UserDemos.module.css";

interface Props {
    uploadId: string;
}

const ShareDemoPanel: React.FC<Props> = ({ uploadId }) => {
    const { t } = useTranslation("pages");
    const [links, setLinks] = useState<ShareLink[]>([]);
    const [open, setOpen] = useState(false);
    const [expiresInDays, setExpiresInDays] = useState<number | "">("");
    const [copied, setCopied] = useState<string | null>(null);

    const load = async () => {
        try { setLinks(await getShareLinks(uploadId)); } catch { /* ignore */ }
    };

    useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

    const fullUrl = (token: string) => `${window.location.origin}/shared-demo/${token}`;

    const create = async () => {
        const link = await createShareLink(uploadId, expiresInDays === "" ? undefined : Number(expiresInDays));
        await load();
        copy(link.token);
    };

    const copy = (token: string) => {
        navigator.clipboard.writeText(fullUrl(token));
        setCopied(token);
        setTimeout(() => setCopied(null), 2000);
    };

    const revoke = async (token: string) => {
        await revokeShareLink(uploadId, token);
        load();
    };

    return (
        <div className={`${styles.surface} mb-3`} style={{ padding: "8px 14px" }}>
            <button className="btn btn-link p-0" onClick={() => setOpen((o) => !o)}>
                {open ? "▾" : "▸"} {t("userDemos.share.toggle")}
            </button>
            {open && (
                <div className="mt-2">
                    <div className="d-flex gap-2 align-items-center mb-2">
                        <input
                            type="number"
                            className={styles.input}
                            style={{ width: 200 }}
                            placeholder={t("userDemos.share.expiresPlaceholder")}
                            value={expiresInDays}
                            onChange={(e) => setExpiresInDays(e.target.value === "" ? "" : Number(e.target.value))}
                            min={1}
                        />
                        <button className="btn btn-primary btn-sm" onClick={create}>{t("userDemos.share.createLink")}</button>
                    </div>
                    {links.length === 0 ? (
                        <div className={`small ${styles.muted}`}>{t("userDemos.share.noLinks")}</div>
                    ) : (
                        <ul className="list-unstyled mb-0">
                            {links.map((l) => (
                                <li key={l.token} className="d-flex justify-content-between align-items-center py-1" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                                    <span className={`small text-truncate ${l.valid ? styles.muted : `${styles.muted} text-decoration-line-through`}`} style={{ maxWidth: 320 }}>
                                            {fullUrl(l.token)}
                                            {l.expiresAt && <span className={`ms-2 ${styles.muted}`}>{t("userDemos.share.expires", { date: new Date(l.expiresAt).toLocaleDateString() })}</span>}
                                        </span>
                                        <span>
                                            {l.valid && (
                                                <>
                                                    <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => copy(l.token)}>
                                                        {copied === l.token ? t("userDemos.share.copied") : t("userDemos.share.copy")}
                                                    </button>
                                                    <button className="btn btn-outline-danger btn-sm" onClick={() => revoke(l.token)}>{t("userDemos.share.revoke")}</button>
                                                </>
                                            )}
                                            {!l.valid && <span className="badge bg-secondary">{t("userDemos.share.revokedExpired")}</span>}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
        </div>
    );
};

export default ShareDemoPanel;
